import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import type { MediaBrowseCatalogResponse, MediaItemSummary } from '@fetmode/types';
import { t } from '@fetmode/i18n';
import { Link } from 'react-router-dom';

import { useAuth } from '../auth/index.js';
import {
  COMPACT_GRID_PAGE_SIZE,
  ThumbGridPager,
  usePagedItems,
} from '../../shared/components/ThumbGridPager/index.js';
import '../../shared/styles/thumb-grid.css';
import '../home/HomePage.css';
import './VideosPage.css';

function VideoCard({
  item,
  progressPercent,
}: {
  item: MediaItemSummary;
  progressPercent?: number;
}): ReactElement {
  const priceLabel = item.owned
    ? t('common.videos.owned')
    : item.priceCoins === 0
      ? t('common.videos.free')
      : t('common.videos.coins').replace('{{amount}}', String(item.priceCoins));
  const videoHref = `/@${item.model.username}/v/${item.slug}`;
  const isFree = !item.owned && item.priceCoins === 0;

  return (
    <article
      className={`thumb-card ${item.owned ? 'thumb-card--owned' : isFree ? 'thumb-card--owned' : 'thumb-card--price'}`}
    >
      <Link to={videoHref} className="thumb-card__media-link" aria-label={item.title}>
        <div className={`thumb-card__thumb${item.thumbnailUrl ? '' : ' thumb-card__thumb--empty'}`}>
          {item.thumbnailUrl ? (
            <img src={item.thumbnailUrl} alt="" loading="lazy" width={640} height={360} />
          ) : null}
          <span
            className={`thumb-card__badge ${item.owned || isFree ? 'thumb-card__badge--owned' : 'thumb-card__badge--price'}`}
          >
            {priceLabel}
          </span>
          {progressPercent !== undefined && progressPercent > 0 ? (
            <div
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                height: 3,
                background: 'rgba(255,255,255,0.2)',
              }}
            >
              <div
                style={{
                  width: `${progressPercent}%`,
                  height: '100%',
                  background: 'var(--fm-color-accent)',
                }}
              />
            </div>
          ) : null}
          <div className="thumb-card__meta">
            <p className="thumb-card__subtitle">{item.model.displayName}</p>
            <h2 className="thumb-card__title">{item.title}</h2>
          </div>
        </div>
      </Link>
    </article>
  );
}

function CatalogRow({
  title,
  children,
  empty,
  page,
  total,
  onPageChange,
}: {
  title: string;
  children: ReactElement | ReactElement[];
  empty?: boolean;
  page: number;
  total: number;
  onPageChange: (page: number) => void;
}): ReactElement | null {
  if (empty) return null;
  return (
    <section style={{ marginBottom: '1.75rem' }}>
      <h2 style={{ margin: '0 0 0.75rem', fontSize: '1.125rem' }}>{title}</h2>
      <div className="videos-catalog-row">
        {children}
      </div>
      <ThumbGridPager
        page={page}
        total={total}
        pageSize={COMPACT_GRID_PAGE_SIZE}
        label={title}
        onPageChange={onPageChange}
      />
    </section>
  );
}

function PagedCatalogRow<T>({
  title,
  items,
  render,
}: {
  title: string;
  items: readonly T[];
  render: (item: T) => ReactElement;
}): ReactElement | null {
  const paging = usePagedItems(items, COMPACT_GRID_PAGE_SIZE);
  return (
    <CatalogRow
      title={title}
      empty={items.length === 0}
      page={paging.page}
      total={paging.total}
      onPageChange={paging.setPage}
    >
      {paging.pageItems.map(render)}
    </CatalogRow>
  );
}

export function VideosPage(): ReactElement {
  const { api } = useAuth();
  const [catalog, setCatalog] = useState<MediaBrowseCatalogResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    void api
      .listMediaBrowseCatalog()
      .then((result) => {
        if (!cancelled) {
          setCatalog(result);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(t('common.videos.loadError'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [api]);

  const isEmpty =
    catalog &&
    catalog.continueWatching.length === 0 &&
    catalog.trending.length === 0 &&
    catalog.movies.length === 0 &&
    catalog.newClips.length === 0 &&
    catalog.fromFollowing.length === 0;

  return (
    <section className="browse-page">
      <div className="browse-page__content">
        {isLoading && (
          <p style={{ color: 'var(--fm-color-text-muted)' }}>{t('common.videos.loading')}</p>
        )}
        {error && <p className="fm-error">{error}</p>}

        {!isLoading && isEmpty && (
          <div className="fm-card">
            <p style={{ margin: 0 }}>{t('common.videos.empty')}</p>
          </div>
        )}

        {catalog ? (
          <>
            <PagedCatalogRow
              title={t('common.videos.rowContinue')}
              items={catalog.continueWatching}
              render={(entry) => (
                <VideoCard
                  key={entry.item.id}
                  item={entry.item}
                  progressPercent={entry.progressPercent}
                />
              )}
            />
            <PagedCatalogRow
              title={t('common.videos.rowTrending')}
              items={catalog.trending}
              render={(item) => <VideoCard key={item.id} item={item} />}
            />
            <PagedCatalogRow
              title={t('common.videos.rowMovies')}
              items={catalog.movies}
              render={(item) => <VideoCard key={item.id} item={item} />}
            />
            <PagedCatalogRow
              title={t('common.videos.rowNewClips')}
              items={catalog.newClips}
              render={(item) => <VideoCard key={item.id} item={item} />}
            />
            <PagedCatalogRow
              title={t('common.videos.rowFollowing')}
              items={catalog.fromFollowing}
              render={(item) => <VideoCard key={item.id} item={item} />}
            />
          </>
        ) : null}
      </div>
    </section>
  );
}
